import axios from 'axios';
import { basicAuthConfig } from '../configs';
import { urlMyspreadApi } from '../urls';

/**
 * API para gestión de la revisión manual de rutas de NS no encontradas en DispatchTrack
 * Maneja la comunicación con el backend para listar y marcar como revisadas las rutas
 * que el proceso de reconciliación diario no pudo emparejar automáticamente.
 *
 * Contrato de referencia (ver docs_myspread/revision_ns/Adicion_Revision_Manual_y_Resumen_Diario.md, §1.2
 * y docs_myspread/revision_ns/Adicion_Trigger_Manual_Por_Fecha.md, §2-§3):
 *   GET  /mantenedores/ns-reconciliacion/rutas-pendientes-revision?revisado=false
 *   PUT  /mantenedores/ns-reconciliacion/rutas-pendientes-revision/<id>/marcar-revisado
 *   POST /mantenedores/ns-reconciliacion/ejecutar-manual
 *
 * NOTA: al momento de construir este servicio el blueprint de backend correspondiente
 * (routes/mantenedores/ns_reconciliacion_revision.py) aún no existía en el repo — este
 * archivo se construyó contra el contrato documentado. Si el shape real de la respuesta
 * difiere (ej. nombres de campos, envoltorio de paginación), solo hay que ajustar el
 * mapeo de `response.data` de las funciones de abajo.
 */

// Desde el rediseño asíncrono (ver docs_myspread/revision_ns/Adicion_Rediseno_Async_Trigger_Manual.md,
// §3.2 y §5), el backend ya no ejecuta la reconciliación de forma síncrona: solo encola la
// solicitud (Service Bus) y responde en segundos (202). El timeout de axios ya no necesita
// cubrir minutos de ejecución real - se deja un margen razonable de red (30s, mismo valor
// sugerido para el proxy de myspread-flask en §4.2 del documento).
const TIMEOUT_EJECUTAR_MANUAL_MS = 30000;

/**
 * Obtener el listado de rutas pendientes de revisión manual (o todas, según filtro)
 * @param {Object} user - Usuario autenticado
 * @param {Object} filtros - { revisado, fecha_desde, fecha_hasta }
 *   - revisado: boolean (default false en el backend si se omite)
 *   - fecha_desde / fecha_hasta: 'YYYY-MM-DD' (opcional)
 * @returns {Promise} Response con lista de rutas pendientes de revisión
 */
export const getRutasPendientesRevision = async (user, filtros = {}) => {
  try {
    const config = basicAuthConfig(user);
    let endpoint = urlMyspreadApi.replace(/\/$/, '') + '/mantenedores/ns-reconciliacion/rutas-pendientes-revision?';
    const query = [];

    if (filtros.revisado !== undefined && filtros.revisado !== null) {
      query.push(`revisado=${filtros.revisado}`);
    }
    if (filtros.fecha_desde) query.push(`fecha_desde=${filtros.fecha_desde}`);
    if (filtros.fecha_hasta) query.push(`fecha_hasta=${filtros.fecha_hasta}`);

    endpoint += query.join('&');
    const response = await axios.get(endpoint, config);

    if (response.data) {
      return {
        success: true,
        data: response.data.data || [],
        total: response.data.total,
        message: response.data.message
      };
    }

    return {
      success: false,
      error: 'No se recibieron datos del servidor',
      data: []
    };
  } catch (error) {
    console.error('Error al obtener rutas pendientes de revisión:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.response?.data?.message || 'Error al obtener rutas pendientes de revisión',
      data: []
    };
  }
};

/**
 * Obtener el detalle ACTUAL (no el snapshot original detectado) de guías no-definitivas
 * de la ruta/fecha asociada a una fila pendiente de revisión, con su subestado vigente.
 * @param {Object} user - Usuario autenticado
 * @param {number|string} id - ID de la fila de ns_reconciliacion_rutas_pendientes
 * @returns {Promise} `{ success: true, data: { ruta_id, ruta, fecha, guias: [{guia_id, guia, sub_estado}] } }`
 */
export const getGuiasDeRutaPendiente = async (user, id) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/ns-reconciliacion/rutas-pendientes-revision/${id}/guias`;

    const response = await axios.get(endpoint, config);

    return {
      success: true,
      data: response.data?.data || null
    };
  } catch (error) {
    console.error('Error al obtener las guías de la ruta pendiente:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.response?.data?.message || 'Error al obtener las guías de la ruta'
    };
  }
};

/**
 * Marcar una ruta pendiente como revisada
 * @param {Object} user - Usuario autenticado
 * @param {number|string} id - ID de la fila de ns_reconciliacion_rutas_pendientes
 * @param {string} [observaciones] - Observaciones opcionales de quien revisó
 * @returns {Promise} Response con resultado de la operación
 */
export const marcarRutaPendienteRevisada = async (user, id, observaciones) => {
  try {
    const config = basicAuthConfig(user);
    const endpoint = `${urlMyspreadApi.replace(/\/$/, '')}/mantenedores/ns-reconciliacion/rutas-pendientes-revision/${id}/marcar-revisado`;

    const body = {};
    if (observaciones) body.observaciones = observaciones;

    const response = await axios.put(endpoint, body, config);

    return {
      success: true,
      data: response.data.data || {},
      message: response.data.message || 'Ruta marcada como revisada exitosamente'
    };
  } catch (error) {
    console.error('Error al marcar la ruta como revisada:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.response?.data?.message || 'Error al marcar la ruta como revisada'
    };
  }
};

/**
 * Disparar manualmente el proceso de reconciliación de NS para una fecha específica (sin
 * esperar a la corrida automática de la madrugada).
 *
 * Desde el rediseño asíncrono (ver docs_myspread/revision_ns/Adicion_Rediseno_Async_Trigger_Manual.md,
 * §3.2), el backend ya no ejecuta la reconciliación de forma síncrona: solo valida la fecha,
 * encola la solicitud en Service Bus y responde de inmediato con status 202. La respuesta de
 * éxito ya NO trae las métricas finales (`rutas_revisadas`, `guias_candidatas`, etc. - esas
 * se calculan después, en background) - viene `{ success: true, fecha, origen, mensaje }`.
 * `axios` acepta 200-299 como éxito por defecto, por lo que el 202 no requiere manejo especial.
 *
 * @param {Object} user - Usuario autenticado
 * @param {string} fecha - Fecha objetivo en formato 'YYYY-MM-DD' (no puede ser futura)
 * @returns {Promise} `{ success: true, fecha, origen, mensaje }` en éxito (202), o
 *   `{ success: false, error, status }` en falla (400/502 documentados en el backend).
 */
export const ejecutarReconciliacionManual = async (user, fecha) => {
  try {
    const config = { ...basicAuthConfig(user), timeout: TIMEOUT_EJECUTAR_MANUAL_MS };
    const endpoint = urlMyspreadApi.replace(/\/$/, '') + '/mantenedores/ns-reconciliacion/ejecutar-manual';

    const response = await axios.post(endpoint, { fecha }, config);

    if (response.data) {
      return {
        success: response.data.success !== false,
        ...response.data
      };
    }

    return {
      success: false,
      error: 'No se recibieron datos del servidor'
    };
  } catch (error) {
    console.error('Error al ejecutar la reconciliación manual de NS:', error);
    return {
      success: false,
      error: error.response?.data?.error || error.response?.data?.message || 'Error al ejecutar la reconciliación manual de NS',
      status: error.response?.status
    };
  }
};
